import moment from "moment";
import { TypedEmitter } from "tiny-typed-emitter";
import * as timer from "worker-timers";
import {
  AudioPlaylistEntry,
  BasePlaylistEntry,
  Playlist,
  PlaylistEntry,
  PlaylistLayoutEvent,
  RenderablePlaylistEntry,
} from "../interfaces/playlist";
import { Safe4 } from "../interfaces/safe4";

export interface PlaylistServiceEvents {
  "playlist-updated": (event: PlaylistLayoutEvent) => void;
  "safe4-updated": () => void;
}

type RegisteredLayoutEvents = {
  playlistId: number;
  nextTriggerInMillis: number;
  startDateTime: moment.Moment;
  endDateTime?: moment.Moment;
};
class PlaylistService extends TypedEmitter<PlaylistServiceEvents> {
  public safe4Data: Safe4 | null = null;
  private lastEtag = "";
  private refreshLoopInitialized = -1;
  private registeredLayoutEvents = new Map<number, RegisteredLayoutEvents>();

  get isInititalized() {
    return this.safe4Data !== null;
  }
  constructor() {
    super();
    this.on("safe4-updated", () => {
      this.setTrackingAndCss();
    });
  }

  setTrackingAndCss() {
    // TODO evaluate for the future
    /*document.getElementById("tracking-code-head")!.innerText = this.getTrackingCodeHead()?.replace(/[\r\n]/g, "") || "";
    document.getElementById("tracking-code-body")!.innerHTML =
      this.getTrackingCodeBody()?.replace(/[\r\n]/g, "") || "";
    const bodyScripts = document.getElementById("tracking-code-body")!.getElementsByTagName("script");
    for (const script of bodyScripts) {
      eval(script.innerHTML);
    }*/
    document.getElementById("external-css-style")!.innerText = this.getCssStyling()?.replace(/[\r\n]/g, "") || "";
  }

  async initialLoad() {
    if (this.isInititalized) {
      return;
    }
    await this.loadSafe4();
    this.registerLayoutEvents();
    this.reshuffleData();
    this.startRefreshLoop();
  }

  async loadEmptyFallback() {
    this.safe4Data = this.getSafe4WithFallback();
    this.emit("safe4-updated");
  }

  private getEmptyFallbackSafe4(safe4?: Safe4): Safe4 {
    const hasPlaylist = safe4?.playlistContainer?.playlists != null;
    const fallbackImageUrl = hasPlaylist
      ? safe4?.playerContainer?.modelSpecific?.statusImageNoPlayableMedia?.[0]?.url ||
        `${process.env.VUE_APP_API_BASE_URL || ""}images/no_valid_media.webp`
      : safe4?.playerContainer?.modelSpecific?.statusImageMissingPlaylist?.[0]?.url ||
        `${process.env.VUE_APP_API_BASE_URL || ""}images/no_playlist.webp`;
    const fallbackImageFilename = hasPlaylist
      ? safe4?.playerContainer?.modelSpecific?.statusImageNoPlayableMedia?.[0]?.filename || "no_valid_media.webp"
      : safe4?.playerContainer?.modelSpecific?.statusImageMissingPlaylist?.[0]?.filename || "no_playlist.webp";

    return {
      status: "ok",
      playerContainer: {
        name: "WebPlayerTest",
        refreshCommunicationFile: 900,
        refreshContent: 900,
        resolutionWidth: 1920,
        resolutionHeight: 1080,
        screenRotation: 0,
        volume: 50,
        modelType: "WebPlayer",
        modelSpecific: {
          overlayImage: [],
          statusImageNoPlayableMedia: safe4?.playerContainer?.modelSpecific?.statusImageNoPlayableMedia || [],
          statusImageMissingPlaylist: safe4?.playerContainer?.modelSpecific?.statusImageMissingPlaylist || [],
          trackingCodeHead: safe4?.playerContainer?.modelSpecific?.trackingCodeHead || "",
          trackingCodeBody: safe4?.playerContainer?.modelSpecific?.trackingCodeBody || "",
          cssStyling: safe4?.playerContainer?.modelSpecific?.cssStyling || "",
        },
        startScreenLayoutId: 0,
      },
      playlistContainer: {
        playlists: [
          {
            id: 0,
            type: "master_playlist",
            name: "Videos",
            entries: [
              {
                id: 0,
                type: "content",
                contentId: 0,
                duration: 99999999,
              },
            ],
            transition: {
              transitionType: "CrossFade",
              duration: 0,
              totalDuration: 0,
            },
            shuffledEntries: [],
          },
        ],
        playlistsHash: fallbackImageUrl,
      },
      contentContainer: {
        content: [
          {
            id: 0,
            type: "image",
            prefetch: true,
            externalMedia: false,
            url: fallbackImageUrl,
            filename: fallbackImageFilename,
            filesize: 0,
            width: 1920,
            height: 1080,
            thumbnail_url: fallbackImageUrl,
            image_url: fallbackImageUrl,
          },
        ],
      },
      layoutContainer: {
        layouts: [],
      },
      layoutEventsContainer: {
        layoutEvents: [],
      },
    } as Safe4;
  }

  getSafe4WithFallback(safe4?: Safe4): Safe4 {
    if (
      !safe4 ||
      !safe4.playlistContainer ||
      !safe4.playlistContainer.playlists ||
      safe4.playlistContainer.playlists.length === 0
    ) {
      return this.getEmptyFallbackSafe4(safe4);
    }
    return safe4;
  }

  getInitialPlaylist(initialMasterPlaylist?: boolean): Playlist {
    if (!initialMasterPlaylist) {
      const currentLayoutEvent = this.safe4Data?.layoutEventsContainer?.layoutEvents
        .filter((event) => event.end_date)
        .find((event) =>
          moment().isBetween(
            moment(`${event.start_date} ${event.start_time}`),
            moment(`${event.end_date} ${event.end_time}`)
          )
        );

      const layout = this.safe4Data?.layoutContainer.layouts.find(
        (layout) => layout.id === currentLayoutEvent?.layoutId
      );
      if (currentLayoutEvent) {
        // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
        return this.safe4Data?.playlistContainer.playlists.find((p) => p.id === layout?.zones[0].playlistId)!;
      } else {
        if (this.safe4Data?.playerContainer.startScreenLayoutId) {
          const playlistId = this.safe4Data?.layoutContainer.layouts.find(
            (l) => l.id === this.safe4Data?.playerContainer.startScreenLayoutId
          )?.zones[0].playlistId;
          return this.getPlaylistById(playlistId!);
        }
      }
    }
    // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
    const masterPlaylists = this.safe4Data?.playlistContainer.playlists.filter(
      (playlist) => playlist.type === "master_playlist"
    );
    return masterPlaylists?.find((playlist) => playlist.entries?.length > 0) || masterPlaylists![0];
  }

  getPlaylistById(playlistId: number) {
    // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
    return this.safe4Data?.playlistContainer.playlists.find((playlist) => playlist.id === playlistId)!;
  }

  private registerLayoutEvents() {
    const events: RegisteredLayoutEvents[] | undefined = this.safe4Data?.layoutEventsContainer?.layoutEvents.flatMap(
      (event) => {
        let startDateTime;
        const initialStartDateTime = moment(`${event.start_date} ${event.start_time}`);
        if (event.repetition && event.eventType === "forcedStart") {
          const repetitionTime = moment(event.start_time, "HH:mm");
          startDateTime = moment().minutes(repetitionTime.minutes()).seconds(0);
          switch (event.repetition_type) {
            case "hourly":
              startDateTime = startDateTime.add(repetitionTime.minutes() <= moment().minutes() ? 1 : 0, "hours");
              break;
            case "daily":
              startDateTime = startDateTime.hours(repetitionTime.hours());
              startDateTime = startDateTime.add(repetitionTime.hours() <= moment().hours() ? 1 : 0, "day");
              break;
            case "weekly":
              startDateTime = startDateTime.hours(repetitionTime.hours());
              startDateTime = startDateTime.add(repetitionTime.hours() <= moment().hours() ? 1 : 0, "day");
              startDateTime = startDateTime.weekday(initialStartDateTime.weekday());
              startDateTime = startDateTime.add(initialStartDateTime.weekday() <= moment().weekday() ? 1 : 0, "week");
              break;
          }
        } else {
          startDateTime = initialStartDateTime;
        }
        const endDateTime = event.end_date ? moment(`${event.end_date} ${event.end_time}`) : undefined;
        const nextTriggerInMillis = startDateTime.diff(moment());
        const layout = this.safe4Data?.layoutContainer.layouts.find((layout) => layout.id === event.layoutId);
        const zone = layout?.zones[0];

        return {
          playlistId: zone!.playlistId,
          nextTriggerInMillis,
          startDateTime,
          endDateTime,
        };
      }
    );

    if (events) {
      for (const event of events) {
        if (event.nextTriggerInMillis > 0) {
          if (!this.registeredLayoutEvents.has(event.playlistId)) {
            this.registeredLayoutEvents.set(event.playlistId, event);
            //console.log("register", event);
            timer.setTimeout(() => {
              this.emit("playlist-updated", event);
              this.registeredLayoutEvents.delete(event.playlistId);
              this.registerLayoutEvents();
            }, event.nextTriggerInMillis);
          }
        }
        if (event.endDateTime && event.endDateTime?.isAfter(moment())) {
          const initialMasterPlaylist = this.getInitialPlaylist(true);
          if (!this.registeredLayoutEvents.has(initialMasterPlaylist.id)) {
            const endEvent: RegisteredLayoutEvents = {
              playlistId: initialMasterPlaylist.id,
              startDateTime: event.endDateTime,
              nextTriggerInMillis: Math.abs(moment().diff(event.endDateTime)),
            };
            this.registeredLayoutEvents.set(endEvent.playlistId, endEvent);
            //console.log("trigger endevent", endEvent);
            timer.setTimeout(() => {
              this.emit("playlist-updated", endEvent);
              this.registeredLayoutEvents.delete(event.playlistId);
            }, endEvent.nextTriggerInMillis);
          }
        }
      }
    }
  }

  getAudioPlaylistEntries(): AudioPlaylistEntry[] {
    if (!this.safe4Data?.playerContainer?.startAudioPlaylistId) {
      return [];
    }
    const audioPlaylist = this.safe4Data?.playlistContainer.playlists.find(
      (p) => p.id === this.safe4Data?.playerContainer?.startAudioPlaylistId
    );
    if (!audioPlaylist) {
      return [];
    }
    return this.getPlaylistEntries(audioPlaylist);
  }

  getPlayerVolume() {
    return (this.safe4Data?.playerContainer.volume || 100) / 100;
  }

  getPlaylistEntries(playlist: Playlist): BasePlaylistEntry[] {
    let playlistEntries: BasePlaylistEntry[] = [];
    if (playlist) {
      for (const [index, playlistEntry] of playlist.entries.entries()) {
        const data = this.getPlaylistElement(
          playlistEntry,
          playlist,
          index + 1 < playlist.entries.length ? playlist.entries[index + 1] : undefined
        );

        if (data) {
          playlistEntries = playlistEntries.concat(data);
        }
      }
      return playlistEntries;
    }
    return [];
  }

  getPlaylistElement(
    currentEntry: PlaylistEntry,
    playlist: Playlist,
    nextEntry?: PlaylistEntry
  ): RenderablePlaylistEntry[] {
    let contentItem;
    if (currentEntry.type === "content") {
      if (this.isActive(currentEntry)) {
        contentItem = this.safe4Data?.contentContainer.content.find((content) => content.id === currentEntry.contentId);
        if (!contentItem || !contentItem.url) {
          console.warn("Skipping content item: ", contentItem);
          return [];
        }
        return [
          {
            contentItem,
            playlistEntry: currentEntry,
            transition: nextEntry && nextEntry.type === "transition" ? nextEntry.transition : playlist.transition,
          },
        ];
      }
      return [];
    } else if (currentEntry.type === "playlist") {
      const playlist = this.safe4Data?.playlistContainer.playlists.find(
        (playlist) => playlist.id === currentEntry.playlistId
      );
      if (!playlist) {
        return [];
      }
      if (currentEntry.randomPlay) {
        if (playlist.shuffledEntries.length > 0) {
          const randomPlayCount = currentEntry.randomPlayCount || 1;
          //console.log('randomplay count: ', randomPlayCount);
          const channelItems = playlist.shuffledEntries
            .slice(0, randomPlayCount)
            .map((item) => {
              //console.log('item', item);
              const elem = this.getPlaylistElement(item, playlist);
              //console.log('elems: ', elem);
              return elem;
            })
            .reduce((a, b) => {
              const concat = a.concat(b);
              return concat;
            });
          //console.log('entries: ', playlist.shuffledEntries);
          //console.log('result items: ', channelItems);
          return channelItems;
        }
      } else if (
        playlist?.type === "internal_playlist" ||
        playlist?.type === "channel" ||
        playlist?.type === "master_playlist"
      ) {
        return this.getPlaylistEntries(playlist);
      }
      return [];
    }
    return [];
  }

  reshuffleData(): void {
    if (
      this.safe4Data?.playlistContainer &&
      this.safe4Data?.playlistContainer.playlists &&
      this.safe4Data?.playlistContainer.playlists.length
    ) {
      for (const playlist of this.safe4Data?.playlistContainer.playlists) {
        playlist.shuffledEntries = playlist.entries
          .slice()
          .filter(this.isActive)
          .sort(() => 0.5 - Math.random());
      }
    }
  }

  isActive(playlistEntry: PlaylistEntry): boolean {
    if (!playlistEntry.scheduleInformation) {
      return true;
    }
    const scheduleInformation = playlistEntry.scheduleInformation;

    const now = moment();
    const start = moment(scheduleInformation.start_date);
    const end = moment(scheduleInformation.end_date);
    const startTime = moment(scheduleInformation.start_time, "HH:mm");
    const endTime = moment(scheduleInformation.end_time, "HH:mm");

    const weekday = now.weekday();
    const allowedWeekdays: number[] = [];
    if (scheduleInformation.monday) {
      allowedWeekdays.push(1);
    }
    if (scheduleInformation.tuesday) {
      allowedWeekdays.push(2);
    }
    if (scheduleInformation.wednesday) {
      allowedWeekdays.push(3);
    }
    if (scheduleInformation.thursday) {
      allowedWeekdays.push(4);
    }
    if (scheduleInformation.friday) {
      allowedWeekdays.push(5);
    }
    if (scheduleInformation.saturday) {
      allowedWeekdays.push(6);
    }
    if (scheduleInformation.sunday) {
      allowedWeekdays.push(0);
    }

    const date = now.isBetween(start, end, "date", "[]");
    const time = now.isBetween(startTime, endTime, undefined, "[]");
    const day = allowedWeekdays.findIndex((i) => i === weekday) !== -1;
    return date && time && day;
  }

  getOverlay() {
    if (
      !this.safe4Data?.playerContainer.modelSpecific ||
      !this.safe4Data?.playerContainer.modelSpecific.overlayImage ||
      this.safe4Data?.playerContainer.modelSpecific.overlayImage.length === 0
    ) {
      return null;
    }
    return this.safe4Data?.playerContainer.modelSpecific.overlayImage[0];
  }

  getCssStyling() {
    if (
      !this.safe4Data?.playerContainer.modelSpecific ||
      !this.safe4Data?.playerContainer.modelSpecific.cssStyling ||
      this.safe4Data?.playerContainer.modelSpecific.cssStyling.length === 0
    ) {
      return null;
    }
    return this.safe4Data?.playerContainer.modelSpecific.cssStyling;
  }

  getTrackingCodeBody() {
    if (
      !this.safe4Data?.playerContainer.modelSpecific ||
      !this.safe4Data?.playerContainer.modelSpecific.trackingCodeBody ||
      this.safe4Data?.playerContainer.modelSpecific.trackingCodeBody.length === 0
    ) {
      return null;
    }
    return this.safe4Data?.playerContainer.modelSpecific.trackingCodeBody;
  }

  getTrackingCodeHead() {
    if (
      !this.safe4Data?.playerContainer.modelSpecific ||
      !this.safe4Data?.playerContainer.modelSpecific.trackingCodeHead ||
      this.safe4Data?.playerContainer.modelSpecific.trackingCodeHead.length === 0
    ) {
      return null;
    }
    return this.safe4Data?.playerContainer.modelSpecific.trackingCodeHead;
  }

  startRefreshLoop() {
    if (this.safe4Data?.playerContainer.refreshCommunicationFile && this.refreshLoopInitialized === -1) {
      this.refreshLoopInitialized = timer.setInterval(async () => {
        await this.loadSafe4();
      }, 10000);
    }
  }

  async loadSafe4() {
    if (process.env.NODE_ENV !== "production" && !(window as any).safe4File) {
      (window as any).safe4File = require("../../tests/data/safe4/bad_füssing_tv.json");
    }
    if ((window as any).safe4File) {
      if (!this.safe4Data) {
        this.safe4Data = this.getSafe4WithFallback((window as any).safe4File);
        this.reshuffleData();
        this.emit("safe4-updated");
      } else {
        const newSafe4 = this.getSafe4WithFallback((window as any).safe4File);
        const newSafe4Hash = newSafe4.playlistContainer?.playlistsHash || "";
        const existingSafe4Hash = this.safe4Data?.playlistContainer?.playlistsHash || "";
        if (newSafe4Hash !== existingSafe4Hash) {
          this.safe4Data = newSafe4;
          this.reshuffleData();
          this.emit("safe4-updated");
        }
      }
      return;
    }
    const paths = location.pathname.split("/");
    const safe4Id = paths[paths.length - 1];
    // no valid uuid so simply use empty fallback
    if (!safe4Id || safe4Id === "webplayer") {
      (window as any).safe4File = this.getSafe4WithFallback();
      this.loadSafe4();
      return;
    }
    // when running in preview mode, the safe4Id is the playlist id, which is a number
    const url = isNaN(safe4Id as any)
      ? `${process.env.VUE_APP_API_BASE_URL || location.origin}/v1/safe4/${safe4Id}`
      : `${process.env.VUE_APP_API_BASE_URL || location.origin}/v1/safe4/preview/${safe4Id}`;

    if (url.startsWith("file://")) {
      console.info("Running in local file mode, loading empty fallback");
      this.safe4Data = this.getSafe4WithFallback();
      this.emit("safe4-updated");
      return;
    }
    let loadNew = true;
    if (this.lastEtag !== "") {
      try {
        const response = await fetch(url, {
          method: "HEAD",
          headers: {
            "If-None-Match": this.lastEtag,
          },
        });
        loadNew = response.status !== 304;
      } catch (err) {
        console.error("Error during HEAD request:", err);
        loadNew = true;
      }
    }

    if (loadNew) {
      try {
        const response = await fetch(url);
        if (!response.ok) {
          throw new Error(`Failed to fetch data: ${response.statusText}`);
        }
        this.lastEtag = response.headers.get("etag") || "";
        const data = await response.json();
        const newSafe4: Safe4 = this.getSafe4WithFallback(data);
        if (newSafe4.playlistContainer.playlistsHash !== this.safe4Data?.playlistContainer.playlistsHash) {
          this.safe4Data = newSafe4;
          this.reshuffleData();
          this.emit("safe4-updated");
        }
      } catch (err) {
        console.error("Error during GET request:", err);
      }
    }
  }

  getPlaylistHash() {
    return this.safe4Data?.playlistContainer.playlistsHash;
  }
}

export const playlistService = new PlaylistService();
